import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { env } from '../../core/util/env';
import { useAppContext } from '../../core/context';
import { useCallback } from 'react';
import Loader from '../../lib/components/Loader';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import { interactionEvents } from '../../core/services/mixpanelEvents';
import { useAppOpener } from '../../platform/app-opener/useAppOpener';
import { useNavigation } from '../../platform/navigation/useNavigation';
import { useFlag } from '../../feature-flags';
import { calculateOpenInAppVariant } from '../../core/util/openInAppVariant';

export const OpenVippsApp = ({ onClick }: { onClick: () => void }) => {
  const context = useAppContext();
  const languageContext = useLanguageContext();
  const appOpener = useAppOpener();
  const navigation = useNavigation();
  const openInAppVariant = calculateOpenInAppVariant(useFlag('fancyDeeplink'));

  const openAndroidIntentLink = useCallback(() => {
    try {
      onClick();
      context?.mixpanel?.trackInteraction(
        interactionEvents.LANDING_PAGE_GOOGLE_INTENT_LINK_CLICKED
      );
      appOpener.openAndroidIntent();
    } catch {
      console.warn('CAUGHT not able to open android intent url');
    }
  }, [context, onClick, appOpener]);

  if (!context.claims) {
    return <Loader />;
  }

  const openUniversalLink = () => {
    try {
      onClick();
      const token = navigation.getCurrentUrl().searchParams.get('token');
      const result = appOpener.openUniversalLink(token!, context!.claims!.market);

      context?.mixpanel?.trackInteraction(interactionEvents.LANDING_PAGE_UNIVERSAL_LINK_CLICKED);

      if (result.blocked) {
        context?.mixpanel?.trackInteraction(interactionEvents.LANDING_PAGE_UNIVERSAL_LINK_FAILED);
        console.warn('not able to open fancy new url, trying next approach');
        navigation.assign(result.url);
      }
    } catch {
      context?.mixpanel?.trackInteraction(interactionEvents.LANDING_PAGE_UNIVERSAL_LINK_FAILED);

      console.warn('CAUGHT not able to open universal link url');
    }
  };
  const openCustomSchemeLink = () => {
    try {
      onClick();
      const token = navigation.getCurrentUrl().searchParams.get('token');

      context?.mixpanel?.trackInteraction(
        interactionEvents.LANDING_PAGE_CUSTOM_SCHEME_LINK_CLICKED
      );

      appOpener.openCustomScheme(token!, '_top');
    } catch {
      // noop
    }
  };

  const openAppLink = () => {
    switch (openInAppVariant) {
      case 'GoogleIntent':
        openAndroidIntentLink();
        return;
      case 'Universal':
        openUniversalLink();
        return;
      default:
        openUniversalLink();
    }
  };

  if (env.DEV || env.UAT)
    return (
      <>
        <PrimaryButton
          loading={!context}
          onClick={openCustomSchemeLink}
          aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
          disabled={false}
        >
          Open vipps:// Link
        </PrimaryButton>
        <PrimaryButton
          loading={!context}
          onClick={openUniversalLink}
          aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
          disabled={false}
        >
          Open Universal Link
        </PrimaryButton>
        <PrimaryButton
          loading={!context}
          onClick={openAndroidIntentLink}
          aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
          disabled={false}
        >
          Open Android Intent
        </PrimaryButton>
        <PrimaryButton
          loading={!context}
          onClick={onClick}
          aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
          disabled={false}
        >
          Show phone number input
        </PrimaryButton>
      </>
    );

  return (
    <PrimaryButton
      loading={!context}
      onClick={openAppLink}
      aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
      disabled={false}
    >
      {languageContext.getLocalizedText('inputPrimary')}
    </PrimaryButton>
  );
};
